import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ThumbsUp, ThumbsDown, Share2, Bookmark, Flag, BadgeCheck, ShoppingBag, MessageSquare, Send, Trash2,
  Loader2, Download, ExternalLink, Play } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR } from "../../lib/api";
import MPlayShell, { Avatar, VCard, fmtViews, timeAgo } from "./VidShared";
import { useAuth } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { AdSlot, AdNetworkScripts, AdPreRoll, useAdConfig } from "../../ads/AdEngine";
import { trackAppEvent } from "../../lib/pwaAnalytics";

const REPORT_REASONS = [
  ["copyright_movie", "Copyright / Movie Content"], ["spam", "Spam"], ["fake_misleading", "Fake / Misleading Content"],
  ["scam", "Scam"], ["illegal", "Illegal Content"], ["inappropriate", "Inappropriate Content"],
  ["unrelated", "Unrelated Content"], ["other", "Other"],
];

const viewerId = () => {
  try {
    let id = localStorage.getItem("mp_viewer");
    if (!id) { id = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("mp_viewer", id); }
    return id;
  } catch { return `anon${Date.now()}`; }
};

function track(key, slug) {
  try {
    const arr = JSON.parse(localStorage.getItem(key) || "[]").filter((s) => s !== slug);
    arr.unshift(slug);
    localStorage.setItem(key, JSON.stringify(arr.slice(0, 50)));
  } catch { /* ignore */ }
}

export default function VideoWatch() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [preroll, setPreroll] = useState(false);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [comments, setComments] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [busyC, setBusyC] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busyR, setBusyR] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const playerRef = useRef(null);
  const viewTracked = useRef(false);
  const likeBusy = useRef(false);
  const adCfg = useAdConfig("mplay", "watch");

  useEffect(() => {
    setPlaying(false); setPreroll(false); setV(null); setNotFound(false); setComments(null); setShowFullDesc(false);
    viewTracked.current = false;
    setLiked(false); setDisliked(false);
    api.get(`/vid/videos/${slug}`).then((r) => {
      setV(r.data);
      setFollowers(r.data.followers || 0);
      track("mp_history", slug);
      try {
        setSaved(JSON.parse(localStorage.getItem("mp_later") || "[]").includes(slug));
        setLiked(!!r.data.liked);
      } catch { /* ignore */ }
      api.get(`/vid/videos/${r.data.id}/comments`).then((c) => setComments(c.data.comments)).catch(() => setComments([]));
      if (user) api.get(`/vid/creators/${r.data.creator_id}/follow-status`).then((s) => setFollowing(s.data.following)).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [slug, user]);

  useEffect(() => { if (v) document.title = `${v.title} | MPlay`; }, [v]);

  // genuine-view tracking: count only after the viewer actually watches min_watch_seconds
  useEffect(() => {
    if (!playing || !v || viewTracked.current) return;
    const minSec = v.min_watch_seconds ?? 8;
    const t = setTimeout(async () => {
      viewTracked.current = true;
      try {
        const { data } = await api.post(`/vid/videos/${slug}/view`, { viewer_id: viewerId(), watched_seconds: minSec });
        if (data.counted) setV((prev) => (prev ? { ...prev, views: data.views } : prev));
      } catch { /* ignore */ }
    }, minSec * 1000);
    return () => clearTimeout(t);
  }, [playing, v, slug]);

  const toggleFollow = async () => {
    if (!user) { toast.error("Please log in to continue."); navigate(`/mkt/auth?mode=login&returnTo=${encodeURIComponent(`/v/${slug}`)}`); return; }
    try {
      const { data } = await api.post(`/vid/creators/${v.creator_id}/follow`);
      setFollowing(data.following); setFollowers(data.followers);
      toast.success(data.following ? `Following ${v.creator_name}` : "Unfollowed");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const like = async () => {
    if (!user) { toast.error("Please log in to continue."); navigate(`/mkt/auth?mode=login&returnTo=${encodeURIComponent(`/v/${slug}`)}`); return; }
    if (likeBusy.current) return;
    likeBusy.current = true;
    const prev = liked;
    setLiked(!prev); if (!prev) setDisliked(false);
    try {
      const { data } = await api.post(`/vid/videos/${v.id}/like`);
      setLiked(data.liked);
      setV((cur) => ({ ...cur, likes: data.likes }));
    } catch (e) {
      setLiked(prev);
      if (e?.response?.status === 401) { toast.error("Please log in to continue."); navigate(`/mkt/auth?mode=login&returnTo=${encodeURIComponent(`/v/${slug}`)}`); }
    } finally { likeBusy.current = false; }
  };

  const save = () => {
    const next = !saved;
    setSaved(next);
    try {
      const arr = JSON.parse(localStorage.getItem("mp_later") || "[]").filter((s) => s !== slug);
      if (next) arr.unshift(slug);
      localStorage.setItem("mp_later", JSON.stringify(arr));
    } catch { /* ignore */ }
    toast.success(next ? "Saved to Watch later" : "Removed from Watch later");
  };

  const share = async () => {
    const url = window.location.href;
    try { await navigator.share({ title: v.title, url }); }
    catch { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };

  const postComment = async () => {
    if (!user) { toast.error("Please log in to continue."); navigate(`/mkt/auth?mode=login&returnTo=${encodeURIComponent(`/v/${slug}`)}`); return; }
    if (!commentText.trim() || busyC) return;
    setBusyC(true);
    try {
      const { data } = await api.post(`/vid/videos/${v.id}/comments`, { text: commentText.trim() });
      setComments([data.comment, ...(comments || [])]);
      setV({ ...v, comment_count: (v.comment_count || 0) + 1 });
      setCommentText("");
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Please log in to continue."); navigate(`/mkt/auth?mode=login&returnTo=${encodeURIComponent(`/v/${slug}`)}`); }
      else toast.error(errMsg(e));
    } finally { setBusyC(false); }
  };

  const delComment = async (cid) => {
    try {
      await api.delete(`/vid/comments/${cid}`);
      setComments(comments.filter((c) => c.id !== cid));
      setV({ ...v, comment_count: Math.max(0, (v.comment_count || 1) - 1) });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitReport = async () => {
    if (!reason) { toast.error("Select a reason"); return; }
    setBusyR(true);
    try {
      await api.post(`/vid/videos/${v.id}/report`, { reason, details });
      toast.success("Report submitted — our team will review it");
      setReportOpen(false); setReason(""); setDetails("");
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Please login to report a video");
      else toast.error(errMsg(e));
    } finally { setBusyR(false); }
  };

  const play = () => {
    // smart ad engine: show pre-roll overlay before starting the actual video,
    // but never more than once per cooldown window — normal clicks must not trigger ads
    let allowPreroll = !!(adCfg?.enabled && adCfg?.preroll);
    if (allowPreroll) {
      try {
        const last = Number(localStorage.getItem("mp_preroll_ts") || 0);
        const cd = (adCfg.preroll.cooldown_minutes ?? 10) * 60000;
        if (Date.now() - last < cd) allowPreroll = false;
      } catch { /* ignore */ }
    }
    if (allowPreroll) setPreroll(true);
    else setPlaying(true);
    trackAppEvent("video_start", { video_id: v?.id || "" });
    playerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const prerollDone = () => {
    try { localStorage.setItem("mp_preroll_ts", String(Date.now())); } catch { /* ignore */ }
    setPreroll(false);
    setPlaying(true);
    trackAppEvent("video_start", { video_id: v?.id || "" });
  };

  if (notFound) {
    return (
      <MPlayShell>
        <div className="flex flex-col items-center justify-center py-32 gap-4" data-testid="watch-notfound">
          <Play className="h-12 w-12 text-emerald-400" />
          <p className="text-white font-extrabold text-xl">Video unavailable</p>
          <Link to="/videos" className="mp-green h-11 px-7 rounded-full text-sm font-extrabold inline-flex items-center" data-testid="watch-home">Back to MPlay</Link>
        </div>
      </MPlayShell>
    );
  }
  if (!v) {
    return <MPlayShell><div className="py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div></MPlayShell>;
  }

  const ActionBtn = ({ icon: Icon, label, active, onClick, testid, count }) => (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12.5px] font-bold transition-colors ${active ? "mp-green" : "bg-white/[0.07] hover:bg-white/[0.14] text-white"}`}>
      <Icon className={`h-4 w-4 ${active ? "fill-current" : ""}`} />{label}{count !== undefined ? ` ${fmtViews(count)}` : ""}
    </button>
  );

  return (
    <MPlayShell noPad>
      <AdNetworkScripts slug="mplay" page="watch" />
      <div className="max-w-[1750px] mx-auto px-0 sm:px-6 pt-0 sm:pt-5 pb-24 lg:pb-10 grid lg:grid-cols-[1fr_380px] gap-6" data-testid="watch-page">
        {/* ===== LEFT: player + info + comments ===== */}
        <div className="min-w-0">
          <div ref={playerRef} className="relative bg-black sm:rounded-2xl overflow-hidden aspect-video" data-testid="watch-player">
            {preroll && !playing && adCfg?.preroll && (
              <AdPreRoll ad={adCfg.preroll.ad} skipDelay={adCfg.preroll.skip_delay} maxDuration={adCfg.preroll.max_duration} slug="mplay" onDone={prerollDone} />
            )}
            {playing ? (
              <iframe src={`${v.embed_url}${v.embed_url.includes("?") ? "&" : "?"}autoplay=1&playsinline=1`} title={v.title}
                className="h-full w-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="watch-iframe" />
            ) : (
              <button onClick={play} className="absolute inset-0 w-full h-full group" data-testid="watch-play">
                {v.thumbnail_url
                  ? <img src={v.thumbnail_url} alt={v.title} className="h-full w-full object-cover" />
                  : <div className="h-full w-full" style={{ background: "linear-gradient(140deg,#0f2a1a,#123a5f)" }} />}
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="h-16 w-16 sm:h-20 sm:w-20 rounded-full mp-green flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                    <Play className="h-8 w-8 fill-current ml-1" />
                  </span>
                </span>
                <span className="mp-kw absolute bottom-3 right-3 bg-black/80 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-md">{fmtViews(v.views)} views</span>
              </button>
            )}
          </div>

          <AdSlot slug="mplay" page="watch" slot="after_hero" />

          <h1 className="text-white font-extrabold text-lg sm:text-xl leading-snug px-3 sm:px-0 mt-4" data-testid="watch-title">{v.title}</h1>
          <p className="text-white/50 text-[12.5px] px-3 sm:px-0 mt-1">{fmtViews(v.views)} views · {timeAgo(v.created_at)}{v.category ? ` · ${v.category}` : ""}</p>

          {/* channel row */}
          <div className="flex flex-wrap items-center gap-3 px-3 sm:px-0 mt-4">
            <div className="flex items-center gap-3 flex-1 min-w-[200px]">
              <Avatar name={v.creator_name} src={v.creator_avatar} size={40} />
              <div className="min-w-0">
                <p className="text-white font-bold text-[14.5px] flex items-center gap-1.5" data-testid="watch-creator">{v.creator_name} <BadgeCheck className="h-4 w-4 text-emerald-400 shrink-0" /></p>
                <p className="text-white/50 text-[12px]" data-testid="watch-followers">{fmtViews(followers)} followers</p>
              </div>
              {user?.id !== v.creator_id && (
                <button onClick={toggleFollow} data-testid="watch-follow"
                  className={`h-9 px-5 rounded-full text-[13px] font-extrabold transition-all ${following ? "bg-white/[0.1] text-white hover:bg-white/[0.16]" : "mp-green"}`}>
                  {following ? "Following" : "Follow"}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center bg-white/[0.07] rounded-full overflow-hidden">
                <button onClick={like} data-testid="watch-like"
                  className={`flex items-center gap-1.5 h-9 px-4 text-[12.5px] font-bold transition-colors hover:bg-white/[0.1] ${liked ? "text-emerald-400" : "text-white"}`}>
                  <ThumbsUp className={`h-4 w-4 ${liked ? "fill-current" : ""}`} />{fmtViews(v.likes || 0)}
                </button>
                <span className="w-px h-5 bg-white/15" />
                <button onClick={() => setDisliked(!disliked)} data-testid="watch-dislike"
                  className={`h-9 px-4 transition-colors hover:bg-white/[0.1] ${disliked ? "text-emerald-400" : "text-white"}`}>
                  <ThumbsDown className={`h-4 w-4 ${disliked ? "fill-current" : ""}`} />
                </button>
              </div>
              <ActionBtn icon={Share2} label="Share" onClick={share} testid="watch-share" />
              <ActionBtn icon={Bookmark} label={saved ? "Saved" : "Save"} active={saved} onClick={save} testid="watch-save" />
              <a href={v.source_url} target="_blank" rel="noreferrer" data-testid="watch-source"
                className="flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12.5px] font-bold bg-white/[0.07] hover:bg-white/[0.14] text-white transition-colors">
                <ExternalLink className="h-4 w-4" />{v.platform_label || "Source"}
              </a>
              <button onClick={() => setReportOpen(true)} data-testid="watch-report"
                className="h-9 w-9 rounded-full bg-white/[0.07] hover:bg-white/[0.14] text-white/60 hover:text-white flex items-center justify-center transition-colors" title="Report">
                <Flag className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* description */}
          <div className="mp-panel rounded-2xl p-4 mt-4 mx-3 sm:mx-0" data-testid="watch-desc">
            <p className="text-white/80 text-[13.5px] font-semibold">{fmtViews(v.views)} views · {timeAgo(v.created_at)}</p>
            {v.description && (
              <p className={`text-white/65 text-[13.5px] mt-1.5 whitespace-pre-wrap ${showFullDesc ? "" : "line-clamp-2"}`}>{v.description}</p>
            )}
            {v.description && v.description.length > 120 && (
              <button onClick={() => setShowFullDesc(!showFullDesc)} className="text-white text-[13px] font-bold mt-1" data-testid="watch-desc-toggle">
                {showFullDesc ? "Show less" : "…more"}
              </button>
            )}
            {v.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {v.tags.map((t) => <span key={t} className="text-emerald-400 text-[12px] font-bold">#{t}</span>)}
              </div>
            )}
          </div>

          {/* AFFILIATE OFFER CTA — unchanged business feature */}
          {v.offer && (
            <a href={`/api/vid/r/${v.affiliate_code}`} data-testid="watch-offer"
              className="mx-3 sm:mx-0 mt-4 flex items-center gap-4 rounded-2xl p-4 border border-emerald-500/30 bg-emerald-500/[0.07] hover:bg-emerald-500/[0.13] transition-colors group">
              {v.offer.image
                ? <img src={v.offer.image} alt="" className="h-14 w-14 rounded-xl object-cover shrink-0" />
                : <span className="h-14 w-14 rounded-xl mp-green flex items-center justify-center shrink-0"><ShoppingBag className="h-6 w-6" /></span>}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-400">Featured in this video</p>
                <p className="text-white font-extrabold text-[15px] truncate mt-0.5">{v.offer.name}</p>
                <p className="text-white/60 text-xs font-bold">{fmtINR(v.offer.price)} · {v.offer.business_name}</p>
              </div>
              <span className="mp-green h-10 px-5 rounded-full text-[13px] font-extrabold inline-flex items-center shrink-0 group-hover:brightness-110">Get it →</span>
            </a>
          )}

          {/* COMMENTS */}
          <div className="px-3 sm:px-0 mt-8" data-testid="watch-comments">
            <h2 className="text-white font-extrabold text-[17px] flex items-center gap-2.5 mb-5">
              Comments <span className="text-white/45 text-sm font-bold">{fmtViews(v.comment_count || comments?.length || 0)}</span>
            </h2>
            {user ? (
              <div className="flex gap-3 mb-7">
                <Avatar name={user.name} size={38} />
                <div className="flex-1">
                  <input value={commentText} onChange={(e) => setCommentText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postComment()}
                    placeholder="Add a comment…" maxLength={500} data-testid="watch-comment-input"
                    className="w-full bg-transparent border-b border-white/15 focus:border-emerald-400 outline-none text-white text-[14px] py-2 placeholder:text-white/35 transition-colors" />
                  <div className="flex justify-end gap-2 mt-2.5">
                    <button onClick={() => setCommentText("")} className="h-9 px-4 rounded-full text-[13px] font-bold text-white/70 hover:bg-white/10 transition-colors">Cancel</button>
                    <button onClick={postComment} disabled={busyC || !commentText.trim()} data-testid="watch-comment-post"
                      className="h-9 px-5 rounded-full text-[13px] font-extrabold mp-green disabled:opacity-40 inline-flex items-center gap-1.5">
                      {busyC ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-3.5 w-3.5" />Comment</>}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Link to={`/mkt/auth?mode=login&returnTo=${encodeURIComponent(`/v/${slug}`)}`} data-testid="watch-comment-login"
                className="inline-flex items-center gap-2 mb-6 text-emerald-400 text-sm font-bold hover:underline">
                <MessageSquare className="h-4 w-4" /> Sign in to join the conversation
              </Link>
            )}
            <div className="space-y-6">
              {!comments && <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />}
              {comments?.length === 0 && <p className="text-white/45 text-sm">No comments yet — start the conversation.</p>}
              {comments?.map((c) => (
                <div key={c.id} className="flex gap-3" data-testid={`watch-comment-${c.id}`}>
                  <Avatar name={c.name} size={36} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px]"><span className="text-white font-bold">@{c.name?.replace(/\s+/g, "").toLowerCase()}</span> <span className="text-white/40"> · {timeAgo(c.created_at)}</span></p>
                    <p className="text-white/85 text-[13.5px] mt-1 whitespace-pre-wrap">{c.text}</p>
                  </div>
                  {user && (user.id === c.user_id || user.id === v.creator_id || user.role === "ROOT_SUPER_ADMIN") && (
                    <button onClick={() => delComment(c.id)} data-testid={`watch-comment-del-${c.id}`} className="text-white/30 hover:text-red-400 transition-colors shrink-0 self-start"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <AdSlot slug="mplay" page="watch" slot="footer" />
        </div>

        {/* ===== RIGHT: related rail ===== */}
        <aside className="px-3 sm:px-0 lg:pr-1" data-testid="watch-related">
          <AdSlot slug="mplay" page="watch" slot="sidebar" />
          <p className="text-white font-extrabold text-[15px] mb-3.5 hidden lg:block">Up next</p>
          <div className="space-y-3.5">
            {(v.related || []).map((r) => (
              <Link key={r.id} to={`/v/${r.slug}`} className="flex gap-2.5 group" data-testid={`watch-next-${r.id}`}>
                <div className="mp-thumb w-[168px] aspect-video shrink-0">
                  {r.thumbnail_url
                    ? <img src={r.thumbnail_url} alt="" loading="lazy" className="h-full w-full object-cover" />
                    : <div className="h-full w-full flex items-center justify-center"><Play className="h-6 w-6 text-white/20 fill-white/20" /></div>}
                  <span className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{fmtViews(r.views)}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-white text-[13px] font-bold leading-snug line-clamp-2 group-hover:text-emerald-400 transition-colors">{r.title}</p>
                  <p className="text-white/50 text-[11.5px] mt-1">{r.creator_name}</p>
                  <p className="text-white/50 text-[11.5px]">{fmtViews(r.views)} views · {timeAgo(r.created_at)}</p>
                </div>
              </Link>
            ))}
          </div>
        </aside>
      </div>

      {/* REPORT DIALOG */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader><DialogTitle>Report Video</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Reports go to the MILTROO admin team for review.</p>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="report-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>{REPORT_REASONS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`report-reason-${k}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea data-testid="report-details" value={details} onChange={(e) => setDetails(e.target.value)} rows={3} placeholder="Optional details…" />
            <button onClick={submitReport} disabled={busyR || !reason} data-testid="report-submit"
              className="w-full h-11 rounded-full font-extrabold text-[14px] mp-green disabled:opacity-40 inline-flex items-center justify-center">
              {busyR ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Report"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </MPlayShell>
  );
}
